import React from 'react';
import type { PoConsumption } from '../../../services/purchaseOrderService';
import { currency } from '../../vendors/utils/format';

/**
 * Spend against a purchase order's ceiling (HE-P07).
 *
 * Two tones, because "approved" and "submitted" are different facts about the same money:
 * solid is payable, hatched is booked but still awaiting form approval. Collapsing them into a
 * single figure either overstates what is owed or hides work that has already happened.
 *
 * Over-run gets its own treatment rather than a bar clipped at 100%. A PO past its ceiling is
 * the single most important thing this control can say, and a full bar looks identical to a
 * PO that landed exactly on budget.
 */
export interface ConsumptionBarProps {
  consumption?: PoConsumption | null;
  /** Compact form for table rows: bar plus a percentage, no figure breakdown. */
  compact?: boolean;
  /** Taller bar for the consumption modal and other summary surfaces. */
  size?: 'sm' | 'lg';
  /** Breakdown under the bar. Defaults to on when not compact. */
  showLegend?: boolean;
  /** When set, the bar is a button (list page opens the consumption modal). */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

const pct = (value: number, of: number) => (of > 0 ? (value / of) * 100 : 0);

export const ConsumptionBar: React.FC<ConsumptionBarProps> = ({
  consumption,
  compact = false,
  size = 'sm',
  showLegend,
  onClick,
}) => {
  // No figure at all is not the same as zero spend — say so rather than drawing an empty bar.
  if (!consumption || consumption.pending) {
    return (
      <span className="tw-text-sm tw-text-content-muted" title="Consumption cannot be calculated yet">
        Not available
      </span>
    );
  }

  const { approved, submitted, amount } = consumption;
  const used = approved + submitted;
  const overrun = used > amount && amount > 0;
  const legend = showLegend ?? !compact;

  // Widths are of the *bar*, so an over-run scales against what was actually spent and the
  // ceiling is drawn as a marker inside it instead of the bar silently saturating.
  const basis = overrun ? used : amount;
  const approvedPct = pct(approved, basis);
  const submittedPct = pct(submitted, basis);
  const ceilingPct = overrun ? pct(amount, used) : 100;
  const usedPct = Math.round(pct(used, amount));
  const large = size === 'lg';

  const bar = (
    <div className={compact ? 'tw-min-w-[10rem]' : ''}>
      <div
        className={`tw-relative tw-w-full tw-overflow-hidden tw-bg-surface-hover ${
          large ? 'tw-h-4 tw-rounded-full' : 'tw-h-2 tw-rounded'
        }`}
        role="progressbar"
        aria-valuenow={usedPct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${usedPct}% of ${currency(amount)} used`}
      >
        <div
          className={`tw-absolute tw-inset-y-0 tw-left-0 ${overrun ? 'tw-bg-danger' : 'tw-bg-primary'}`}
          style={{ width: `${approvedPct}%` }}
        />
        <div
          className={`tw-absolute tw-inset-y-0 tw-opacity-40 ${overrun ? 'tw-bg-danger' : 'tw-bg-primary'}`}
          style={{ left: `${approvedPct}%`, width: `${submittedPct}%` }}
        />
        {overrun && (
          // Where the PO's ceiling sits inside an over-run bar.
          <div
            className="tw-absolute tw-inset-y-0 tw-w-px tw-bg-ink"
            style={{ left: `${ceilingPct}%` }}
            title={`Ceiling ${currency(amount)}`}
          />
        )}
      </div>

      {compact ? (
        <div className="tw-mt-1 tw-text-xs tw-text-content-muted">
          {overrun ? (
            <span className="tw-font-semibold tw-text-danger">
              {usedPct}% – over by {currency(used - amount)}
            </span>
          ) : (
            <>
              {usedPct}% of {currency(amount)}
            </>
          )}
        </div>
      ) : legend ? (
        <div className="tw-mt-2 tw-flex tw-flex-wrap tw-gap-x-4 tw-gap-y-1 tw-text-sm">
          <span>
            <span
              className={`tw-mr-1 tw-inline-block tw-h-2 tw-w-2 tw-rounded-sm ${
                overrun ? 'tw-bg-danger' : 'tw-bg-primary'
              }`}
            />
            Approved {currency(approved)}
          </span>
          <span>
            <span
              className={`tw-mr-1 tw-inline-block tw-h-2 tw-w-2 tw-rounded-sm tw-opacity-40 ${
                overrun ? 'tw-bg-danger' : 'tw-bg-primary'
              }`}
            />
            Submitted {currency(submitted)}
          </span>
          <span className={overrun ? 'tw-font-semibold tw-text-danger' : 'tw-text-content-muted'}>
            {overrun
              ? `Over by ${currency(used - amount)}`
              : `Remaining ${currency(consumption.remaining)}`}
          </span>
        </div>
      ) : null}
    </div>
  );

  if (!onClick) return bar;

  return (
    <button
      type="button"
      onClick={(event) => {
        // The list row navigates to the detail page; opening the modal must not.
        event.stopPropagation();
        onClick(event);
      }}
      title="View what's consuming this purchase order"
      aria-label="View consumption details"
      className="tw-w-full tw-border-0 tw-bg-transparent tw-p-1 tw-text-left tw-rounded hover:tw-bg-surface-hover focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-line-focus"
    >
      {bar}
    </button>
  );
};

export default ConsumptionBar;
